import React, { useState, useEffect } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { TextInput as PaperTextInput, useTheme } from 'react-native-paper';
import { useAuth } from '../context/AuthContext';
import TruckService from '../services/truckService';
import { Truck } from '../types/Truck';
import { ErrorLogger } from '../utils/errorLogger';

interface TruckDropdownProps {
  selectedTruck: Truck | null;
  onTruckSelect: (truck: Truck | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

const TruckDropdown: React.FC<TruckDropdownProps> = ({
  selectedTruck,
  onTruckSelect,
  label = 'Truck',
  placeholder = 'Select a truck',
  required = false,
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const [availableTrucks, setAvailableTrucks] = useState<Truck[]>([]);
  const [showTruckMenu, setShowTruckMenu] = useState(false);

  // Load available trucks when component mounts
  useEffect(() => {
    const loadTrucks = async () => {
      if (user?.uid) {
        try {
          const trucks = await TruckService.getAllActiveTrucks();
          setAvailableTrucks(trucks);
          // Auto-select if only one truck and nothing selected yet
          if (trucks.length === 1 && !selectedTruck) {
            onTruckSelect(trucks[0]);
          }
        } catch (error) {
          ErrorLogger.logError(error as any, 'TruckDropdown.loadTrucks');
        }
      }
    };

    loadTrucks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const displayLabel = required ? `${label} *` : label;

  return (
    <View style={styles.container}>
      <Pressable
        onPress={() => {
          console.log(
            'Truck selector pressed, available trucks:',
            availableTrucks.length,
          );
          setShowTruckMenu(!showTruckMenu);
        }}
        style={styles.truckSelector}
      >
        <PaperTextInput
          mode="flat"
          label={displayLabel}
          value={selectedTruck ? selectedTruck.plateNumber : ''}
          placeholder={placeholder}
          editable={false}
          pointerEvents="none"
          right={
            <PaperTextInput.Icon
              icon={showTruckMenu ? 'chevron-up' : 'chevron-down'}
            />
          }
          style={[styles.paperInput, { backgroundColor: theme.colors.surfaceVariant }]}
          testID="truck-input"
        />
      </Pressable>

      {showTruckMenu && (
        <View style={[styles.dropdownContainer, { backgroundColor: theme.colors.surface }]}>
          {/* Add "No truck" option for optional selections */}
          {!required && (
            <Pressable
              onPress={() => {
                onTruckSelect(null);
                setShowTruckMenu(false);
              }}
              style={[
                styles.dropdownItem,
                !selectedTruck && { backgroundColor: theme.colors.primaryContainer },
              ]}
            >
              <Text
                style={[
                  styles.dropdownItemText,
                  { color: theme.colors.onSurface },
                  !selectedTruck && { fontFamily: 'Inter-Medium', color: theme.colors.primary },
                ]}
              >
                No truck selected
              </Text>
            </Pressable>
          )}

          {availableTrucks.length === 0 ? (
            <View style={styles.dropdownItem}>
              <Text style={[styles.dropdownItemText, { color: theme.colors.onSurface }]}>
                No active trucks available
              </Text>
            </View>
          ) : (
            availableTrucks.map(truck => (
              <Pressable
                key={truck.id}
                onPress={() => {
                  onTruckSelect(truck);
                  setShowTruckMenu(false);
                }}
                style={[
                  styles.dropdownItem,
                  selectedTruck?.id === truck.id && { backgroundColor: theme.colors.primaryContainer },
                ]}
              >
                <View style={styles.dropdownItemRow}>
                  <MaterialCommunityIcons
                    name="truck"
                    size={18}
                    color={selectedTruck?.id === truck.id ? theme.colors.primary : theme.colors.onSurfaceVariant}
                  />
                  <Text
                    style={[
                      styles.dropdownItemText,
                      { color: theme.colors.onSurface },
                      selectedTruck?.id === truck.id && { fontFamily: 'Inter-Medium', color: theme.colors.primary },
                    ]}
                  >
                    {truck.plateNumber}
                  </Text>
                </View>
              </Pressable>
            ))
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  truckSelector: {
    width: '100%',
  },
  paperInput: {},
  dropdownContainer: {
    borderRadius: 6,
    marginTop: 4,
    maxHeight: 200,
    elevation: 4,
    backgroundColor: '#ffffff',
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.06,
    shadowRadius: 40,
    zIndex: 1000,
  },
  dropdownItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: 'rgba(195, 198, 213, 0.15)',
  },
  dropdownItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  dropdownItemText: {
    fontFamily: 'Inter-Regular',
    fontSize: 16,
  },
});

export default TruckDropdown;
